/**
 * LA COMPOSIZIONE DELLA HOME: dodici box numerati, che si riempiono da soli e si spostano a mano.
 *
 * Fonte di verità UNICA della disposizione. Lo stesso modulo lo importano tre posti diversi, e non
 * per eleganza: se lo scheletro vivesse nella pagina e l'admin lo ridisegnasse a mano, la
 * "preview" mostrerebbe una home che non esiste.
 *   - src/app/(frontend)/(home)/page.tsx  → rende i box veri
 *   - src/components/admin/HomepageLayoutEditor.tsx → disegna lo scheletro e la stessa risoluzione
 *   - src/collections/HomepageLayout.ts  → valida il json dei pin
 * Per questo qui NON si importa niente: nessun `payload`, nessun `next`, nessun tipo generato.
 * Le funzioni sono generiche sulla forma minima che serve (RankablePost), così girano identiche
 * sul server (documenti Payload) e nel client (json della REST).
 *
 * ── COME SI DECIDE CHI STA IN UN BOX ──────────────────────────────────────────────────────
 * Due meccanismi sovrapposti, in quest'ordine:
 *   1. I PIN (manuali). `slots` è la lista sparsa dei box fissati dalla redazione: box → articolo.
 *      Un box fissato non si muove mai, qualunque cosa venga pubblicata dopo.
 *   2. L'AUTOMATICO. Tutti i box NON fissati si riempiono in ordine dalla coda di merito
 *      (rankPosts): il primo box libero prende il primo articolo della coda. Da qui viene il
 *      comportamento che serve alla redazione senza fare niente: un articolo appena pubblicato
 *      entra in cima alla coda, quindi si infila nel primo box automatico e spinge giù gli altri.
 *
 * Conseguenza da tenere presente (l'admin la dichiara a schermo): fissare il box 1 significa che i
 * nuovi articoli non andranno più in apertura finché quel pin non viene rilasciato.
 */

export type HomeZone = 'mosaic' | 'grid'

export type HomeBox = {
  /**
   * Il numero che si vede in admin ed è la chiave dei pin salvati. È un IDENTIFICATORE, non una
   * posizione: rinumerare i box sposta i pin già salvati su altri riquadri.
   */
  n: number
  zone: HomeZone
  label: string
  hint: string
}

export const HOME_MOSAIC_BOXES = 3
export const HOME_GRID_BOXES = 9

/**
 * Lo scheletro. L'ordine di questo array È l'ordine di riempimento automatico: il box 1 pesca
 * per primo. Rispecchia il DOM della home (mosaico in evidenza, poi la griglia "Ultimi articoli"),
 * perché è anche l'ordine di importanza editoriale.
 */
export const HOME_BOXES: readonly HomeBox[] = [
  {
    n: 1,
    zone: 'mosaic',
    label: 'Apertura',
    hint: 'Il riquadro grande del mosaico. La sua immagine è l\'LCP della pagina: qui va un articolo con una copertina che regge l\'ingrandimento.',
  },
  { n: 2, zone: 'mosaic', label: 'Spalla alta', hint: 'Colonna di destra del mosaico, in alto.' },
  { n: 3, zone: 'mosaic', label: 'Spalla bassa', hint: 'Colonna di destra del mosaico, in basso.' },
  ...Array.from({ length: HOME_GRID_BOXES }, (_, i) => ({
    n: HOME_MOSAIC_BOXES + 1 + i,
    zone: 'grid' as const,
    label: `Ultimi articoli · ${i + 1}`,
    hint: `Griglia sotto il mosaico, ${i + 1}ª card (riga ${Math.floor(i / 3) + 1}).`,
  })),
]

const HOME_BOX_NUMBERS = new Set(HOME_BOXES.map((b) => b.n))

/**
 * Quanti articoli pescare dal database per riempire i box. Il doppio dei box: serve margine
 * perché i pin consumano posizioni della coda (un pin su un articolo vecchio non toglie nulla al
 * bacino) e perché l'admin deve poter mostrare "chi entra dopo".
 */
export const HOME_POOL_SIZE = HOME_BOXES.length * 2

/**
 * Per quante ore un `placement` promozionale conta. Oltre, l'articolo torna in fila per data.
 *
 * Senza scadenza il meccanismo si rompe da solo: tre articoli marcati "in evidenza" dall'AI
 * occuperebbero il mosaico per sempre, e la home di una testata smetterebbe di raccontare cosa è
 * successo oggi. Il DEMANSIONAMENTO invece non scade: "da fondo pagina" è un giudizio sul pezzo,
 * non sulla sua freschezza.
 */
export const FEATURED_BOOST_HOURS = 72

/**
 * Peso dei valori di `posts.placement` (proiezione di editorialMeta, vedi collections/Posts.ts).
 * Negativo = promozione, positivo = demansionamento, assente = neutro. Un articolo senza
 * placement vale 0, cioè sta in mezzo: è il caso NORMALE oggi (editorialMeta non è ancora
 * valorizzato da nessuna parte in produzione), e per questo l'automatico degrada esattamente
 * nell'ordine per data che la home ha sempre avuto.
 */
const PLACEMENT_WEIGHT: Record<string, number> = {
  featured_top: -2,
  center_top: -1,
  center_bottom: 1,
  right_side: 1,
  footer_center: 2,
}

/** La forma minima che serve per ordinare e assegnare. `Post` la soddisfa già. */
export type RankablePost = {
  id: number
  publishedAt?: string | null
  createdAt?: string | null
  placement?: (string | null)[] | null
}

/** Un pin: il box `box` mostra l'articolo `post`, e non si muove. */
export type HomeSlot = { box: number; post: number }

export type ResolvedBox<T> = {
  box: HomeBox
  post: T | null
  /** true = l'articolo è lì perché fissato a mano; false = riempimento automatico. */
  pinned: boolean
  /**
   * Id fissato che non è più disponibile (articolo tornato in bozza, o cancellato). Il box NON
   * resta vuoto — ricade sull'automatico — ma l'admin lo segnala, altrimenti la redazione crede
   * di avere un pin che in pagina non esiste.
   */
  danglingPin: number | null
}

const safeParse = (raw: string): unknown => {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const timeOf = (post: RankablePost): number => {
  const raw = post.publishedAt || post.createdAt
  const t = raw ? Date.parse(raw) : Number.NaN
  return Number.isFinite(t) ? t : 0
}

/** Il peso editoriale: minore = più in alto. Le promozioni valgono solo finché l'articolo è fresco. */
function weightOf(post: RankablePost, now: number): number {
  const values = Array.isArray(post.placement) ? post.placement : []
  const fresh = now - timeOf(post) <= FEATURED_BOOST_HOURS * 3_600_000
  const weights = values
    .map((v) => (v ? PLACEMENT_WEIGHT[v] : undefined))
    .filter((w): w is number => typeof w === 'number' && (w > 0 || fresh))
  return weights.length ? Math.min(...weights) : 0
}

/**
 * La CODA DI MERITO: peso editoriale, poi data di pubblicazione decrescente, poi id decrescente.
 *
 * L'id in coda all'ordinamento non è pedanteria: due articoli pubblicati nello stesso istante (la
 * pipeline pubblica in batch) altrimenti si ordinerebbero come capita, e l'anteprima dell'admin
 * mostrerebbe una home diversa da quella servita, senza che nessuno capisca perché.
 */
export function rankPosts<T extends RankablePost>(posts: T[], now: number = Date.now()): T[] {
  return posts
    .filter(Boolean)
    .map((post) => ({ post, w: weightOf(post, now), t: timeOf(post) }))
    .sort((a, b) => a.w - b.w || b.t - a.t || b.post.id - a.post.id)
    .map((x) => x.post)
}

/**
 * Ripulisce il json del campo `slots`, che è un json e quindi può contenere qualsiasi cosa
 * (una modifica via API, un pin su un box che non esiste più dopo una rinumerazione).
 *
 * Le due regole di unicità sono il punto: UN articolo per box e UN box per articolo. Senza la
 * seconda, lo stesso pezzo comparirebbe due volte nella stessa schermata — che è il modo più
 * rapido di far sembrare vuota una home piena.
 */
export function normalizeSlots(raw: unknown): HomeSlot[] {
  // Il campo è jsonb: da Postgres torna già decodificato, ma lo stato del form di Payload e un
  // PATCH via REST possono consegnare la stessa lista come STRINGA. Provare a decodificarla costa
  // niente e vale un salvataggio che altrimenti risulterebbe "lista non valida".
  const parsed = typeof raw === 'string' ? safeParse(raw) : raw
  if (!Array.isArray(parsed)) return []
  const byBox = new Map<number, number>()
  const seenPosts = new Set<number>()
  for (const item of parsed) {
    if (!item || typeof item !== 'object') continue
    const box = Number((item as { box?: unknown }).box)
    const post = Number((item as { post?: unknown }).post)
    if (!Number.isInteger(box) || !Number.isInteger(post)) continue
    if (!HOME_BOX_NUMBERS.has(box)) continue
    if (byBox.has(box) || seenPosts.has(post)) continue
    byBox.set(box, post)
    seenPosts.add(post)
  }
  return [...byBox.entries()]
    .map(([box, post]) => ({ box, post }))
    .sort((a, b) => a.box - b.box)
}

/**
 * Chi finisce in quale box. È la funzione che la home rende e che l'admin disegna: due
 * chiamate con gli stessi ingressi danno lo stesso risultato, ed è per questo che l'anteprima
 * dell'admin è un'anteprima e non un disegnino.
 *
 * `posts` è il bacino automatico (gli ultimi pubblicati, HOME_POOL_SIZE). `pinned` porta gli
 * articoli fissati che nel bacino non ci sono — un evergreen di sei mesi fa messo in apertura
 * non è tra gli ultimi venti, e senza questo secondo elenco il suo pin risulterebbe morto.
 */
export function resolveHomeBoxes<T extends RankablePost>(args: {
  posts: T[]
  pinned?: T[]
  slots?: unknown
  boxes?: readonly HomeBox[]
  now?: number
}): ResolvedBox<T>[] {
  const boxes = args.boxes ?? HOME_BOXES
  const slots = normalizeSlots(args.slots)

  // Disponibili = bacino + fissati fuori bacino. Chi non è qui non è pubblicato (le due query
  // filtrano su status=published), quindi il suo pin è dangling.
  const available = new Map<number, T>()
  for (const post of [...(args.pinned ?? []), ...args.posts]) {
    if (post) available.set(post.id, post)
  }

  const pinnedByBox = new Map(slots.map((s) => [s.box, s.post]))
  const taken = new Set(slots.map((s) => s.post).filter((id) => available.has(id)))

  const queue = rankPosts(args.posts, args.now ?? Date.now()).filter((p) => !taken.has(p.id))
  let next = 0

  return boxes.map((box) => {
    const pin = pinnedByBox.get(box.n)
    if (pin != null) {
      const post = available.get(pin)
      if (post) return { box, post, pinned: true, danglingPin: null }
      return { box, post: queue[next++] ?? null, pinned: false, danglingPin: pin }
    }
    return { box, post: queue[next++] ?? null, pinned: false, danglingPin: null }
  })
}

/** Fissa `postId` nel box `box`, liberando il box e sfilando l'articolo da dove stava prima. */
export function pinToBox(slots: unknown, box: number, postId: number): HomeSlot[] {
  const clean = normalizeSlots(slots).filter((s) => s.box !== box && s.post !== postId)
  return normalizeSlots([...clean, { box, post: postId }])
}

/** Rilascia il box: torna automatico. */
export function releaseBox(slots: unknown, box: number): HomeSlot[] {
  return normalizeSlots(slots).filter((s) => s.box !== box)
}

/**
 * Scambia il contenuto di due box. Entrambi restano FISSATI, anche quello che prima era
 * automatico: se non fosse così, l'articolo appena spostato verrebbe rimesso in fila dalla coda
 * di merito e finirebbe altrove: uno "sposta" che non sposta.
 */
export function swapBoxes<T extends RankablePost>(
  slots: unknown,
  resolved: ResolvedBox<T>[],
  a: number,
  b: number,
): HomeSlot[] {
  if (a === b) return normalizeSlots(slots)
  const postIn = (n: number) => resolved.find((r) => r.box.n === n)?.post?.id
  const pa = postIn(a)
  const pb = postIn(b)
  let out = normalizeSlots(slots).filter((s) => s.box !== a && s.box !== b)
  if (pb != null) out = pinToBox(out, a, pb)
  if (pa != null) out = pinToBox(out, b, pa)
  return out
}
